import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useRealtimeEvent } from "../lib/realtime";
import {
  X,
  Plus,
  Trash2,
  Check,
} from "@/components/icons";

/**
 * ChatPopover — Popover tipo MSN que abre hacia arriba desde el footer.
 * Lista separada por Conectados / Desconectados, con buscador para agregar
 * doctores por email. Al tocar un contacto se abre la conversación.
 */
export default function ChatPopover({ open, onClose, onOpenChat }) {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const isStaff = ["doctor", "secretary", "admin"].includes(user?.role);

  // Load contacts + pending requests when opened
  const loadContacts = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get("/chat/contacts"),
        api.get("/chat/contact-requests").catch(() => ({ data: [] })),
      ]);
      setContacts(c.data || []);
      setRequests(r.data || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!open || !isStaff) return;
    loadContacts();
    const t = setInterval(loadContacts, 20000); // refresh presence
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isStaff]);

  // Realtime: refresh contact list unread count on new messages
  useRealtimeEvent("chat:new", () => {
    if (open) loadContacts();
  });

  // Realtime: nueva solicitud de contacto o aceptación
  useRealtimeEvent("chat:contact_request", () => {
    if (open) loadContacts();
  });
  useRealtimeEvent("chat:contact_accepted", () => {
    if (open) loadContacts();
  });

  const addByEmail = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v) return;
    setAdding(true);
    try {
      const { data } = await api.post("/chat/contacts", { email: v });
      if (data.status === "sent") {
        toast.success(`Solicitud enviada a ${data.to?.name || v}. Debe aceptarla para empezar a chatear.`);
      } else if (data.status === "pending") {
        toast.info("Ya hay una solicitud pendiente con este contacto");
      } else if (data.status === "contact") {
        toast.info("Este contacto ya está en tu lista");
      }
      setEmail("");
      await loadContacts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pude enviar la solicitud");
    } finally {
      setAdding(false);
    }
  };

  const acceptRequest = async (req) => {
    try {
      await api.post(`/chat/contact-requests/${req.id}/accept`);
      toast.success(`${req.from_name} ahora es tu contacto`);
      await loadContacts();
    } catch {
      toast.error("No pude aceptar la solicitud");
    }
  };

  const rejectRequest = async (req) => {
    try {
      await api.post(`/chat/contact-requests/${req.id}/reject`);
      toast.success("Solicitud rechazada");
      await loadContacts();
    } catch {
      toast.error("No pude rechazar la solicitud");
    }
  };

  const removeContact = async (c, e) => {
    e.stopPropagation();
    if (c.source !== "added") return;
    if (!window.confirm(`¿Quitar a ${c.name} de tus contactos?`)) return;
    try {
      await api.delete(`/chat/contacts/${c.id}`);
      toast.success("Contacto eliminado");
      await loadContacts();
    } catch {
      toast.error("No pude eliminar");
    }
  };

  if (!open) return null;

  const online = contacts.filter((c) => c.online);
  const offline = contacts.filter((c) => !c.online);

  const Avatar = ({ c, size = 36 }) => (
    <span
      className="rounded-full flex items-center justify-center overflow-hidden relative flex-shrink-0 bg-gradient-to-br from-[#D7D4FF] to-[#F7D6EC]"
      style={{ width: size, height: size }}
    >
      {c.photo_url ? (
        <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" />
      ) : (
        <span className="text-white font-bold" style={{ fontSize: size / 3 }}>
          {(c.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
        </span>
      )}
      {c.online && (
        <span
          className="absolute right-0 bottom-0 w-2.5 h-2.5 rounded-full bg-[#22C55E] ring-2 ring-white"
          data-testid={`online-dot-${c.id}`}
        />
      )}
    </span>
  );

  return (
    <div
      className="fixed z-[55] right-0 bottom-11 w-[380px] h-[560px] bg-white rounded-tl-[22px] shadow-[0_8px_32px_rgba(0,0,0,0.18)] border border-r-0 border-b-0 border-black/[0.06] flex flex-col overflow-hidden"
      data-testid="chat-popover"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-black/[0.06] flex items-center justify-between bg-[#FAFAFC]">
        <div className="text-[14px] font-bold text-black">Mensajes</div>
        <button
          onClick={onClose}
          className="text-black/60 hover:text-black transition"
          data-testid="chat-close"
          type="button"
        >
          <X className="w-4 h-4" strokeWidth={1.75} weight="regular" />
        </button>
      </div>

      {!isStaff && (
        <div className="flex-1 flex items-center justify-center text-sm text-black/60 px-6 text-center">
          El chat está disponible para doctores y secretarias.
        </div>
      )}

      {/* Vista de lista */}
      {isStaff && (
        <>
          {/* Agregar por email */}
          <form
            onSubmit={addByEmail}
            className="px-3 py-2.5 border-b border-black/[0.06] flex items-center gap-2 bg-white"
            data-testid="chat-add-form"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Agregar doctor por email…"
              className="flex-1 bg-[#F2F3F5] rounded-full px-3.5 h-9 outline-none text-[13px] text-black placeholder-black/40 focus:ring-2 focus:ring-[#0071E3]/30 transition"
              data-testid="chat-add-email-input"
            />
            <button
              type="submit"
              disabled={adding || !email.trim()}
              data-testid="chat-add-email-btn"
              className="w-9 h-9 rounded-full bg-[#0071E3] text-white flex items-center justify-center hover:bg-[#0077ED] disabled:opacity-50 transition flex-shrink-0"
            >
              <Plus className="w-4 h-4" strokeWidth={2} weight="regular" />
            </button>
          </form>

          <div className="flex-1 overflow-y-auto">
            {/* Solicitudes pendientes */}
            {requests.length > 0 && (
              <>
                <div className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#0071E3] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0071E3] animate-pulse" />
                  Solicitudes · {requests.length}
                </div>
                {requests.map((r) => (
                  <div
                    key={r.id}
                    data-testid={`chat-request-${r.id}`}
                    className="px-3 py-2 flex items-center gap-3 bg-[#F0F7FF] border-l-2 border-[#0071E3]"
                  >
                    <Avatar
                      c={{ id: r.from_user_id, name: r.from_name, photo_url: r.from_photo_url, online: false }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-black truncate">
                        {r.from_name}
                      </div>
                      <div className="text-[11px] text-black/50 truncate">
                        Te envió una solicitud
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => acceptRequest(r)}
                        data-testid={`chat-request-accept-${r.id}`}
                        title="Aceptar"
                        className="w-7 h-7 rounded-lg bg-[#0071E3] text-white flex items-center justify-center hover:bg-[#0077ED] transition"
                      >
                        <Check className="w-3.5 h-3.5" strokeWidth={2} weight="regular" />
                      </button>
                      <button
                        type="button"
                        onClick={() => rejectRequest(r)}
                        data-testid={`chat-request-reject-${r.id}`}
                        title="Rechazar"
                        className="w-7 h-7 rounded-lg bg-white border border-black/[0.08] text-black/50 flex items-center justify-center hover:bg-black/[0.04] hover:text-red-500 transition"
                      >
                        <X className="w-3.5 h-3.5" strokeWidth={2} weight="regular" />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Conectados */}
            <div className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-black/40 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
              Conectados · {online.length}
            </div>
            {online.length === 0 && (
              <div className="px-4 py-2 text-[12px] text-black/40 italic">
                Nadie conectado ahora
              </div>
            )}
            {online.map((c) => (
              <ContactRow
                key={c.id}
                c={c}
                Avatar={Avatar}
                onClick={() => { onOpenChat?.(c); }}
                onRemove={(e) => removeContact(c, e)}
              />
            ))}

            {/* Desconectados */}
            <div className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-wider text-black/40 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-black/25" />
              Desconectados · {offline.length}
            </div>
            {offline.length === 0 && (
              <div className="px-4 py-2 text-[12px] text-black/40 italic">
                Todos tus contactos están en línea
              </div>
            )}
            {offline.map((c) => (
              <ContactRow
                key={c.id}
                c={c}
                Avatar={Avatar}
                onClick={() => { onOpenChat?.(c); }}
                onRemove={(e) => removeContact(c, e)}
              />
            ))}
            <div className="h-3" />
          </div>
        </>
      )}
    </div>
  );
}

function ContactRow({ c, Avatar, onClick, onRemove }) {
  const subtitle =
    c.last_message
      ? c.last_message.length > 42
        ? c.last_message.slice(0, 42) + "…"
        : c.last_message
      : c.email || (c.role === "doctor" ? "Doctor" : c.role === "secretary" ? "Secretaria" : "");
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`chat-contact-${c.id}`}
      className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-black/[0.03] transition group"
    >
      <Avatar c={c} />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-black truncate">
          {c.name}
          {c.source === "added" && (
            <span className="ml-1.5 text-[9px] font-semibold uppercase tracking-wider text-black/40">
              ·
            </span>
          )}
        </div>
        <div className="text-[11px] text-black/50 truncate">{subtitle}</div>
      </div>
      {c.unread > 0 && (
        <span className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
          {c.unread > 9 ? "9+" : c.unread}
        </span>
      )}
      {c.source === "added" && (
        <button
          type="button"
          onClick={onRemove}
          data-testid={`chat-contact-remove-${c.id}`}
          title="Quitar contacto"
          className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg flex items-center justify-center text-black/40 hover:text-red-500 hover:bg-red-50 transition"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
        </button>
      )}
    </button>
  );
}
